import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import ContextLink from '../components/ContextLink';

/**
 * TodayView — daily operations briefing.
 * Shows: greeting, weather, today's tasks, overdue tasks, alerts.
 * UX Law 1: Most important first. UX Law 4: Context over completeness.
 */
export default function TodayView({ token, weather, onCardTap }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/api/v1/aif/today');
        setData(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" /></div>;
  if (!data) return null;

  const overdue = data.tasks_overdue || [];
  const today = data.tasks_today || [];
  const alerts = data.alerts || [];
  // Weather: prefer prop, then /today response, fallback null
  const w = weather || data.weather;

  return (
    <div className="h-full overflow-y-auto overscroll-contain">
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Greeting + Date */}
        <div>
          <h1 className="text-xl font-semibold text-[var(--aif-text-primary)]">
            {data.greeting}, {data.user_name || t('app.seasonalGreeting')}
          </h1>
          <p className="text-sm text-[var(--aif-text-muted)]">
            {new Date(data.date).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>

        {/* Weather Bar */}
        {w && (
          <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-3 flex items-center gap-3">
            <span className="text-2xl">{w.icon || '\u2600\uFE0F'}</span>
            <div className="flex-1">
              {(w.temperature != null || w.temp_max != null) ? (
                <div className="text-sm font-medium text-[var(--aif-text-primary)]">
                  {w.temperature ?? w.temp_max}{'\u00B0'}C &middot; Wind {w.wind_speed ?? '—'} m/s &middot; {w.precipitation > 0 ? t('today.rainy') : t('today.dry')}
                </div>
              ) : (
                <div className="text-sm italic text-[var(--aif-text-muted)]">
                  {t('weather.unavailable', 'Wetter nicht verfügbar')}
                </div>
              )}
              <div className="text-xs text-[var(--aif-text-muted)]">
                {data.spray_summary ? `${data.spray_summary.sprayable}/${data.spray_summary.total} ${t('today.sprayable')}` : ''}
              </div>
            </div>
          </div>
        )}

        {/* Overdue Tasks */}
        {overdue.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm">{'\u26A0\uFE0F'}</span>
              <span className="text-sm font-semibold text-red-600">{overdue.length} {t('today.overdue')}</span>
            </div>
            {overdue.map(task => (
              <TaskCard key={task.id} task={task} isOverdue onTap={onCardTap} />
            ))}
          </div>
        )}

        {/* Today's Tasks */}
        {today.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-[var(--aif-text-primary)]">
              {'\uD83D\uDCCB'} {today.length} {t('today.tasksToday')}
            </h2>
            {today.map(task => (
              <TaskCard key={task.id} task={task} onTap={onCardTap} />
            ))}
          </div>
        )}

        {/* No Tasks */}
        {overdue.length === 0 && today.length === 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 text-center">
            <div className="text-2xl mb-1">{'\u2705'}</div>
            <p className="text-sm text-green-700 dark:text-green-300">{t('today.allDone')}</p>
          </div>
        )}

        {/* Alerts — always show section */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-[var(--aif-text-secondary)]">{t('today.alerts')}</h2>
          {alerts.length > 0 ? (
            alerts.map((a, i) => (
              <div key={i} className={`bg-[var(--aif-bg-surface)] border rounded-xl px-3 py-2.5 text-sm ${
                a.severity === 'warning' ? 'border-amber-300 text-amber-700 dark:text-amber-300' : 'border-[var(--aif-border-default)] text-[var(--aif-text-secondary)]'
              }`}>
                {a.type === 'LOW_STOCK' ? '\uD83D\uDCE6' : a.type === 'DEADLINE' ? '\uD83D\uDCC5' : '\uD83D\uDCC8'} {a.text}
              </div>
            ))
          ) : (
            <p className="text-xs text-[var(--aif-text-muted)]">{t('today.noAlerts')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskCard({ task, isOverdue, onTap }) {
  const { t } = useTranslation();
  const TYPE_ICONS = { FERTILIZATION: '\uD83C\uDF3F', CROP_PROTECTION: '\uD83E\uDDEA', TILLAGE: '\uD83D\uDE9C', SOWING: '\uD83C\uDF31', HARVEST: '\uD83C\uDF3E' };
  return (
    <button
      onClick={() => onTap?.('weekly', { taskId: task.id })}
      className={`w-full text-left bg-[var(--aif-bg-surface)] border rounded-xl p-3 space-y-1 transition hover:bg-[var(--aif-bg-elevated)] ${
        isOverdue ? 'border-[var(--aif-color-danger-border)]' : 'border-[var(--aif-border-default)]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--aif-text-primary)]">
          {TYPE_ICONS[task.activity_type] || '\uD83D\uDCCB'} {task.title}
        </span>
        {isOverdue && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-color-danger-light)] text-[var(--aif-color-danger)] font-medium">{task.overdue_days}{t('time.daysShort')}</span>}
      </div>
      {task.field_name && (
        task.field_id
          ? <ContextLink type="field" id={task.field_id} label={task.field_name} className="text-xs min-h-0 py-0 px-0" />
          : <div className="text-xs text-[var(--aif-text-muted)]">{task.field_name}</div>
      )}
      <div className="flex items-center gap-3 text-xs text-[var(--aif-text-muted)]">
        {task.machine && <span>{'\uD83D\uDE9C'} {task.machine}</span>}
        {task.quantity && <span>{'\u2696\uFE0F'} {task.quantity}</span>}
      </div>
    </button>
  );
}
